import { useCallback } from 'react';
import { closeAccount, refreshApp } from 'actions';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import AccountLinkedToAutosaveModal from 'features/settings/logins/components/AccountLinkedToAutosaveModal';
import RentReportingLinkedModal from 'features/settings/logins/components/RentReportingLinkedModal';
import { selectRentReportingForAccount } from 'features/rent-reporting/selectors';
import Modal from 'utils/packages/Modal';
import { CLOSE_ACCOUNT_SUCCESS } from 'actions/types';
import { selectConnectionById } from 'reducers/selectors';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { canRenewConnection } from 'features/settings/logins/utils';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import { isWeb } from '../../../constants';
export const useCloseAccount = () => {
    const navigation = useMainStackNavigation();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const refreshAccounts = useRefreshAccounts();
    const savingsPlanAccountId = useAppSelector((state) => state.pots.savingsPlan?.bankInfo?.accountId);
    const navigateToAutosave = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://auto-save', 'Manage Autosave');
            return;
        }
        navigation.navigate('AutoSave');
    }, []);
    const toggleClosed = useCallback(async (account, onSuccess) => {
        const body = { isClosed: !account.isClosed };
        const res = await dispatch(closeAccount(account.id, body));
        if (res?.type === CLOSE_ACCOUNT_SUCCESS) {
            onSuccess?.();
            refreshAccounts(account.type);
            dispatch(refreshApp());
            if (body.isClosed && account.provider !== 'MANUAL') {
                const connection = selectConnectionById(store.getState(), account.bankConnectionId);
                const accounts = connection?.allAccounts;
                const openAccounts = accounts?.filter((account) => !account.isClosed && account.provider !== 'MANUAL');
                if (connection && accounts?.length && !openAccounts?.length) {
                    navigation.navigate('ConnectionAccountsClosed', { connection });
                }
            }
        }
    }, [refreshAccounts]);
    const login = useOpenConnectionLogin(navigation);
    const onOpenLogins = useCallback((data) => login({ type: 'account', data }), [login]);
    const showCloseConfirmation = useCallback((account, onSuccess) => {
        if (account.isHidden) {
            Modal.showConfirmation({
                title: 'Are you sure?',
                subtitle: 'This account will be moved to the Closed section. If you want it back, just reopen it from the account settings.',
                confirmButtonTitle: 'Yes, let me close',
                cancelButtonTitle: 'No, thanks',
                onConfirm: () => toggleClosed(account, onSuccess),
            });
            return;
        }
        Modal.showConfirmation({
            title: 'Are you sure?',
            subtitle: 'This account will be moved to the Closed section but historical data will still be visible across the app. If you want it back, just reopen it from the account settings.',
            confirmButtonTitle: 'Yes, let me close',
            cancelButtonTitle: 'No, thanks',
            onConfirm: () => toggleClosed(account, onSuccess),
        });
    }, [toggleClosed]);
    const handleToggleClosed = useCallback((account, onSuccess) => {
        if (account.isClosed) {
            const connection = selectConnectionById(store.getState(), account.bankConnectionId);
            if (account.provider === 'MANUAL' || !connection) {
                toggleClosed(account, onSuccess);
                return;
            }
            if (connection.isClosed) {
                Modal.showConfirmation({
                    title: 'Reopen this account',
                    subtitle: `To start syncing this account again, you’ll first need to reopen your connection with ${connection.bankInfo.name}. You can do this in your connection settings.`,
                    confirmButtonTitle: 'Not now',
                    cancelButtonTitle: 'Manage connection',
                    onPressCancel: () => {
                        navigation.navigate('BankConnection', {
                            connectionId: connection.id,
                        });
                    },
                });
                return;
            }
            if (account.isRemoved && canRenewConnection(connection)) {
                Modal.showConfirmation({
                    title: 'Reopen this account?',
                    subtitle: 'This account is closed, you need to renew the bank connection to start syncing again',
                    confirmButtonTitle: 'Renew connection',
                    cancelButtonTitle: 'No, thanks',
                    onConfirm: () => {
                        if (isWeb) {
                            UnavailableOnWebModal.show(`emma://bank-connections/${account.bankConnectionId}`, 'Renew connection');
                            return;
                        }
                        onOpenLogins(account);
                    },
                });
                return;
            }
            if (account.isHidden) {
                Modal.showConfirmation({
                    title: 'Reopen this account?',
                    subtitle: 'The account will start syncing again, but its balance and transactions stay invisible unless you unhide because you decided to hide it.',
                    confirmButtonTitle: 'Yes, reopen',
                    cancelButtonTitle: 'No, thanks',
                    onConfirm: () => toggleClosed(account, onSuccess),
                });
                return;
            }
            Modal.showConfirmation({
                title: 'Reopen this account?',
                subtitle: 'The account will start syncing again, and it will show up in your main accounts list',
                confirmButtonTitle: 'Yes, reopen',
                cancelButtonTitle: 'No, thanks',
                onConfirm: () => toggleClosed(account, onSuccess),
            });
            return;
        }
        if (savingsPlanAccountId === account.id) {
            AccountLinkedToAutosaveModal.show(navigateToAutosave, 'close');
            return;
        }
        if (selectRentReportingForAccount(store.getState(), account.id)) {
            RentReportingLinkedModal.show('account', 'close', () => showCloseConfirmation(account, onSuccess));
            return;
        }
        showCloseConfirmation(account, onSuccess);
    }, [
        savingsPlanAccountId,
        toggleClosed,
        onOpenLogins,
        navigateToAutosave,
        showCloseConfirmation,
    ]);
    return handleToggleClosed;
};
